// Centralized date utility for consistent timezone handling
// This ensures all date operations across the application use the configured timezone consistently
// Import timezone configuration from centralized config

import { getAppTimezone, getAppLocale, getTimeFormat } from '../config/timezone';

// Get timezone from centralized config (currently IST, extensible for future)
const IST_TIMEZONE = getAppTimezone(); // 'Asia/Kolkata'
const APP_LOCALE = getAppLocale(); // 'en-IN'

/**
 * Convert any date to IST date string (YYYY-MM-DD)
 * @param {Date|string} date - Date object or date string
 * @returns {string} Date in YYYY-MM-DD format in IST
 */
export const toISTDateString = (date = new Date()) => {
  let dateObj;

  if (typeof date === 'string') {
    // Check if it's an ISO string with time/timezone (contains 'T')
    if (date.includes('T')) {
      // Parse the full ISO string - JavaScript handles timezone correctly
      dateObj = new Date(date);
    } else {
      // Date-only string like "2024-12-02" - create at noon IST to preserve the date
      const [year, month, day] = date.split('-').map(Number);
      // Create date at noon IST (12:00 IST = 06:30 UTC)
      dateObj = new Date(Date.UTC(year, month - 1, day, 6, 30, 0));
    }
  } else {
    dateObj = date;
  }

  // Check for invalid date
  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date';
  }

  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: IST_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });

  return formatter.format(dateObj);
};

/**
 * Convert any date to IST datetime string (YYYY-MM-DD HH:mm:ss)
 * @param {Date|string} date - Date object or date string
 * @returns {string} DateTime in YYYY-MM-DD HH:mm:ss format in IST
 */
export const toISTDateTimeString = (date = new Date()) => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: IST_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  const parts = formatter.formatToParts(dateObj).reduce((acc, part) => {
    if (part.type !== 'literal') {
      acc[part.type] = part.value;
    }
    return acc;
  }, {});

  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
};

/**
 * Parse a date string as IST date
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {Date} Date object set to midnight IST
 */
export const parseISTDate = (dateString) => {
  if (!dateString) return null;

  // If dateString includes time, strip it
  const dateOnly = dateString.split('T')[0];

  // Create date at midnight IST (UTC+5:30)
  // We subtract 5:30 from midnight to get the UTC time
  const [year, month, day] = dateOnly.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));

  // Adjust for IST offset (subtract 5 hours 30 minutes)
  date.setUTCHours(date.getUTCHours() - 5);
  date.setUTCMinutes(date.getUTCMinutes() - 30);

  return date;
};

/**
 * Get current date in IST
 * @returns {string} Current date in YYYY-MM-DD format in IST
 */
export const getCurrentISTDate = () => {
  return toISTDateString(new Date());
};

/**
 * Get IST components from a date
 * @param {Date|string} date - Date object or date string
 * @returns {object} Object with date and time components in IST
 */
export const getISTComponents = (date = new Date()) => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: IST_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    weekday: 'short',
    hour12: false
  });

  const parts = formatter.formatToParts(dateObj).reduce((acc, part) => {
    if (part.type !== 'literal') {
      acc[part.type] = part.value;
    }
    return acc;
  }, {});

  // Map weekday name to day number (0=Sunday, 6=Saturday)
  const dayMap = { 'Sun': 0, 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6 };

  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    time: `${parts.hour}:${parts.minute}:${parts.second}`,
    timestamp: `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`,
    year: parseInt(parts.year),
    month: parseInt(parts.month),
    day: parseInt(parts.day),
    hour: parseInt(parts.hour),
    minute: parseInt(parts.minute),
    second: parseInt(parts.second),
    dayOfWeek: dayMap[parts.weekday] ?? 0,
    weekday: parts.weekday
  };
};

/**
 * Compare if two dates are the same day in IST
 * @param {Date|string} date1
 * @param {Date|string} date2
 * @returns {boolean} True if same day in IST
 */
export const isSameISTDay = (date1, date2) => {
  return toISTDateString(date1) === toISTDateString(date2);
};

/**
 * Format date for display with IST consideration
 * @param {Date|string} date
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string in IST
 */
export const formatISTDate = (date, options = {}) => {
  let dateObj;

  if (typeof date === 'string') {
    // Parse date string directly to avoid UTC timezone shift
    // new Date("YYYY-MM-DD") parses as UTC which shifts the day for IST
    const dateOnly = date.split('T')[0];
    const [year, month, day] = dateOnly.split('-').map(Number);
    // Create date at noon IST (06:30 UTC) to avoid timezone edge cases
    // This is consistent with toISTDateString() approach
    dateObj = new Date(Date.UTC(year, month - 1, day, 6, 30, 0));
  } else {
    dateObj = date;
  }

  const defaultOptions = {
    timeZone: IST_TIMEZONE,
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options
  };

  return new Intl.DateTimeFormat('en-IN', defaultOptions).format(dateObj);
};

/**
 * Convert UTC timestamp from backend to IST date string
 * Backend sends dates like "2025-01-15T18:30:00.000Z" (UTC)
 * We need to convert to IST date "2025-01-16"
 * @param {string} timestamp - UTC timestamp from backend
 * @returns {string} Date in YYYY-MM-DD format in IST
 */
export const getISTDateFromUTC = (timestamp) => {
  if (!timestamp) return null;
  // If it's already a string, pass directly to toISTDateString which handles YYYY-MM-DD correctly
  // This avoids the UTC midnight parsing issue with new Date("YYYY-MM-DD")
  if (typeof timestamp === 'string') {
    return toISTDateString(timestamp);
  }
  return toISTDateString(new Date(timestamp));
};

/**
 * Calculate days between two dates (inclusive) in IST
 * @param {string} fromDate - Start date in YYYY-MM-DD
 * @param {string} toDate - End date in YYYY-MM-DD
 * @returns {number} Number of days (inclusive)
 */
export const calculateDaysBetweenIST = (fromDate, toDate) => {
  const from = parseISTDate(fromDate);
  const to = parseISTDate(toDate);

  if (!from || !to) return 0;

  const diffTime = Math.abs(to - from);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays + 1; // +1 to make it inclusive
};

/**
 * Check if a date falls within a range (inclusive) in IST
 * @param {string} date - Date to check in YYYY-MM-DD
 * @param {string} fromDate - Start date in YYYY-MM-DD
 * @param {string} toDate - End date in YYYY-MM-DD
 * @returns {boolean} True if date is within range
 */
export const isDateInRangeIST = (date, fromDate, toDate) => {
  const checkDate = toISTDateString(date);
  const from = toISTDateString(fromDate);
  const to = toISTDateString(toDate);

  return checkDate >= from && checkDate <= to;
};

/**
 * Format time in IST with specified format
 * @param {Date|string} timestamp - Date object or timestamp string
 * @param {object} options - Formatting options
 * @param {string} options.format - '12h' (default) or '24h'
 * @param {boolean} options.showSeconds - Include seconds (default: false)
 * @param {boolean} options.showIST - Add 'IST' suffix (default: true)
 * @returns {string} Formatted time string
 */
export const formatISTTime = (timestamp, options = {}) => {
  if (!timestamp) return 'N/A';

  const {
    format = '12h',
    showSeconds = false,
    showIST = true
  } = options;

  try {
    const { hour, minute, second } = getISTComponents(new Date(timestamp));

    if (format === '24h') {
      const timeStr = showSeconds
        ? `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`
        : `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      return showIST ? `${timeStr} IST` : timeStr;
    }

    // 12-hour format (default)
    const hour12 = hour % 12 || 12;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const timeStr = showSeconds
      ? `${hour12}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')} ${ampm}`
      : `${hour12}:${String(minute).padStart(2, '0')} ${ampm}`;
    return showIST ? `${timeStr} IST` : timeStr;
  } catch (e) {
    return 'N/A';
  }
};

/**
 * Format date and time together in IST
 * @param {Date|string} timestamp - Date object or timestamp string
 * @param {object} options - Formatting options
 * @param {string} options.dateFormat - 'short', 'long', or 'numeric' (default: 'short')
 * @param {string} options.timeFormat - '12h' or '24h' (default: '12h')
 * @param {boolean} options.showSeconds - Include seconds in time (default: false)
 * @returns {string} Formatted datetime string
 */
export const formatISTDateTime = (timestamp, options = {}) => {
  if (!timestamp) return 'N/A';

  const {
    dateFormat = 'short',
    timeFormat = '12h',
    showSeconds = false
  } = options;

  try {
    const { year, month, day } = getISTComponents(new Date(timestamp));
    const dateObj = new Date(year, month - 1, day);

    let dateStr;
    if (dateFormat === 'long') {
      const weekday = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
      const monthName = dateObj.toLocaleDateString('en-US', { month: 'long' });
      dateStr = `${weekday}, ${monthName} ${day}, ${year}`;
    } else if (dateFormat === 'numeric') {
      dateStr = `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
    } else {
      // short format (default)
      const weekday = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
      const monthName = dateObj.toLocaleDateString('en-US', { month: 'short' });
      dateStr = `${weekday}, ${String(day).padStart(2, '0')} ${monthName} ${year}`;
    }

    const timeStr = formatISTTime(timestamp, { format: timeFormat, showSeconds, showIST: false });
    return `${dateStr} at ${timeStr} IST`;
  } catch (e) {
    return 'N/A';
  }
};

/**
 * Format date for chart display (short format)
 * @param {string|Date} date - Date to format
 * @param {string} format - 'day' (Jan 1), 'month' (Jan 2024), 'year' (2024)
 * @returns {string} Formatted date string for charts
 */
export const formatChartDate = (date, format = 'day') => {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    // Handle invalid dates
    if (isNaN(dateObj.getTime())) return date;

    const { year, month, day } = getISTComponents(dateObj);
    const jsDate = new Date(year, month - 1, day);

    if (format === 'month') {
      const monthName = jsDate.toLocaleDateString('en-US', { month: 'short' });
      return `${monthName} ${year}`;
    } else if (format === 'year') {
      return `${year}`;
    } else {
      // day format (default)
      const monthName = jsDate.toLocaleDateString('en-US', { month: 'short' });
      return `${monthName} ${day}`;
    }
  } catch (e) {
    return date;
  }
};

/**
 * Format date for chart X-axis based on data frequency
 * Automatically determines best format based on date string pattern
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatChartXAxis = (date) => {
  if (!date) return '';

  try {
    const dateStr = typeof date === 'string' ? date : date.toISOString();

    // If date includes time, it's likely daily data
    if (dateStr.includes('T') || dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return formatChartDate(date, 'day');
    }

    // If date is year-month format (YYYY-MM)
    if (dateStr.match(/^\d{4}-\d{2}$/)) {
      return formatChartDate(date, 'month');
    }

    // If date is just year (YYYY)
    if (dateStr.match(/^\d{4}$/)) {
      return formatChartDate(date, 'year');
    }

    // Default to day format
    return formatChartDate(date, 'day');
  } catch (e) {
    return date;
  }
};

// Export IST timezone constant for use in other components
export const IST_TIMEZONE_CONSTANT = IST_TIMEZONE;

// =============================================================================
// SIMPLIFIED FORMATTING FUNCTIONS
// These are the primary functions components should use to replace
// toLocaleTimeString() and toLocaleDateString() calls
// =============================================================================

/**
 * Format time for display (REPLACES toLocaleTimeString)
 * Use this instead of date.toLocaleTimeString()
 * @param {Date|string} time - Date object, timestamp, or time string
 * @param {object} options - Optional formatting options
 * @param {boolean} options.hour12 - Use 12-hour format (default: based on config)
 * @param {boolean} options.showSeconds - Show seconds (default: false)
 * @returns {string} Formatted time string (e.g., "10:30 AM" or "10:30:45 AM")
 */
export const formatTime = (time, options = {}) => {
  if (!time) return '--';

  try {
    // Handle time-only strings like "10:30:00"
    let date;
    if (typeof time === 'string' && /^\d{1,2}:\d{2}(:\d{2})?$/.test(time)) {
      // Time-only string - interpret as IST time
      // Create a date string with the time in IST, then parse it
      const [hours, minutes, seconds = '00'] = time.split(':');
      const h = parseInt(hours);
      const m = parseInt(minutes);
      const s = parseInt(seconds);

      // For time-only strings, we just want to display the time as-is (assuming it's IST)
      // So we format directly without timezone conversion
      const hour12 = options.hour12 ?? (getTimeFormat() === '12h');
      const showSeconds = options.showSeconds ?? false;

      if (hour12) {
        const ampm = h >= 12 ? 'PM' : 'AM';
        const hour12Val = h % 12 || 12;
        if (showSeconds) {
          return `${String(hour12Val).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')} ${ampm}`;
        }
        return `${String(hour12Val).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm}`;
      } else {
        if (showSeconds) {
          return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
        }
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      }
    } else {
      // Full timestamp - parse and convert to IST
      date = time instanceof Date ? time : new Date(time);
    }

    if (isNaN(date.getTime())) return '--';

    const hour12 = options.hour12 ?? (getTimeFormat() === '12h');
    const showSeconds = options.showSeconds ?? false;

    const formatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: hour12,
      timeZone: IST_TIMEZONE
    };

    if (showSeconds) {
      formatOptions.second = '2-digit';
    }

    return date.toLocaleTimeString(APP_LOCALE, formatOptions);
  } catch (e) {
    return '--';
  }
};

/**
 * Format date for display (REPLACES toLocaleDateString)
 * Use this instead of date.toLocaleDateString()
 * @param {Date|string} date - Date object or date string
 * @param {object} options - Optional formatting options
 * @param {string} options.format - 'short' (01/15/2024), 'medium' (Jan 15, 2024), 'long' (January 15, 2024), 'full' (Monday, January 15, 2024)
 * @returns {string} Formatted date string
 */
export const formatDate = (date, options = {}) => {
  if (!date) return '--';

  try {
    let dateObj;

    if (typeof date === 'string') {
      // Parse date string to avoid UTC timezone shift
      const dateOnly = date.split('T')[0];
      const [year, month, day] = dateOnly.split('-').map(Number);
      dateObj = new Date(year, month - 1, day, 12, 0, 0);
    } else {
      dateObj = date;
    }

    if (isNaN(dateObj.getTime())) return '--';

    const format = options.format || 'short';

    let formatOptions = { timeZone: IST_TIMEZONE };

    switch (format) {
      case 'full':
        formatOptions = {
          ...formatOptions,
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        };
        break;
      case 'long':
        formatOptions = {
          ...formatOptions,
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        };
        break;
      case 'medium':
        formatOptions = {
          ...formatOptions,
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        };
        break;
      case 'short':
      default:
        formatOptions = {
          ...formatOptions,
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        };
        break;
    }

    return dateObj.toLocaleDateString(APP_LOCALE, formatOptions);
  } catch (e) {
    return '--';
  }
};

/**
 * Format datetime for display (REPLACES toLocaleString)
 * Use this instead of date.toLocaleString()
 * @param {Date|string} datetime - Date object or timestamp string
 * @param {object} options - Optional formatting options
 * @param {string} options.dateFormat - 'short', 'medium', 'long', 'full'
 * @param {boolean} options.hour12 - Use 12-hour format (default: based on config)
 * @param {boolean} options.showSeconds - Show seconds (default: false)
 * @returns {string} Formatted datetime string
 */
export const formatDateTime = (datetime, options = {}) => {
  if (!datetime) return '--';

  try {
    const date = datetime instanceof Date ? datetime : new Date(datetime);
    if (isNaN(date.getTime())) return '--';

    const hour12 = options.hour12 ?? (getTimeFormat() === '12h');
    const showSeconds = options.showSeconds ?? false;
    const dateFormat = options.dateFormat || 'short';

    let formatOptions = {
      timeZone: IST_TIMEZONE,
      hour: '2-digit',
      minute: '2-digit',
      hour12: hour12
    };

    if (showSeconds) {
      formatOptions.second = '2-digit';
    }

    switch (dateFormat) {
      case 'full':
        formatOptions = {
          ...formatOptions,
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        };
        break;
      case 'long':
        formatOptions = {
          ...formatOptions,
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        };
        break;
      case 'medium':
        formatOptions = {
          ...formatOptions,
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        };
        break;
      case 'short':
      default:
        formatOptions = {
          ...formatOptions,
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        };
        break;
    }

    return date.toLocaleString(APP_LOCALE, formatOptions);
  } catch (e) {
    return '--';
  }
};

/**
 * Get today's date string in app timezone (YYYY-MM-DD format)
 * Use this for getting today's date for forms, API calls, etc.
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export const getTodayDateString = () => {
  return toISTDateString(new Date());
};

/**
 * Get current time formatted for display
 * @param {object} options - Optional formatting options
 * @returns {string} Current time formatted
 */
export const getCurrentTime = () => {
  return formatTime(new Date());
};

/**
 * Get current datetime formatted for display
 * @returns {string} Current datetime formatted
 */
export const getCurrentDateTime = () => {
  return formatDateTime(new Date());
};

/**
 * Format a time string (HH:MM:SS or HH:MM) for display
 * Handles time-only strings that don't have a date component
 * @param {string} timeString - Time string in HH:MM:SS or HH:MM format
 * @param {object} options - Optional formatting options
 * @returns {string} Formatted time string
 */
export const formatTimeString = (timeString, options = {}) => {
  if (!timeString) return '--';

  try {
    // If it's already a full timestamp, use formatTime
    if (timeString.includes('T') || timeString.includes(' ')) {
      return formatTime(timeString, options);
    }

    // Handle HH:MM:SS or HH:MM format
    const parts = timeString.split(':');
    if (parts.length < 2) return '--';

    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const seconds = parts[2] ? parseInt(parts[2], 10) : 0;

    if (isNaN(hours) || isNaN(minutes)) return '--';

    const hour12 = options.hour12 ?? (getTimeFormat() === '12h');
    const showSeconds = options.showSeconds ?? false;

    if (hour12) {
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const hour12Val = hours % 12 || 12;
      const timeStr = showSeconds
        ? `${hour12Val}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} ${ampm}`
        : `${hour12Val}:${String(minutes).padStart(2, '0')} ${ampm}`;
      return timeStr;
    } else {
      const timeStr = showSeconds
        ? `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
        : `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      return timeStr;
    }
  } catch (e) {
    return '--';
  }
};